import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAppContext } from 'src/components/app-context';
import { ScrollElement } from 'src/components/scroll';
import { ListItem } from 'src/components/list-item';
import { PanelHeader, PanelTitle } from '@prisjakt/ui/panel';

import FilterPanel from './components/panel';
import ApplyFilterButton from 'src/components/filter-overlay/components/apply-button';
import SelectedFilters from './components/selected-filters';
import type { Property } from './types';

import {
  toggleMobile,
  propertySearch,
  expandSection,
  propertySearchToggleExpansion,
} from '../../actions/filter';

import * as S from './styles';
import type { AppState } from 'src/reducers';
import { IconChevronDown } from '@prisjakt/ui/icon/chevron-down';
import { IconChevronUp } from '@prisjakt/ui/icon/chevron-up';
import { IconClose } from '@prisjakt/ui/icon/close';

export type FilterProps = {
  categoryId: string;
  brand?: unknown;
  className?: string;
  quickFilters: { name: string; pathName: string }[];
  canCompare?: boolean;
  toggleFilter: () => void;
  productCount?: number;
};

type SearchState = {
  inputValue: string;
  filteredCore: any[];
  filteredSections: Property[];
  filteredValues: any[];
  disabledExpansions: any[];
};

const Filter: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<FilterProps>>
> = props => {
  const { canCompare = true } = props;
  const dispatch = useDispatch();

  React.useEffect(() => {
    return () => {
      dispatch(toggleMobile(false));
      dispatch(propertySearch(''));
    };
  }, [dispatch]);

  const closeMobileFilter = () => {
    window.scroll(0, 0);
    dispatch(toggleMobile(false));
  };

  const filter = useSelector((state: AppState) => state.areas.category.filter);
  const { expandedCategories, mobileOpen } = filter;

  const hasFiltered = Object.keys(filter.query).length > 0;

  const { t } = useAppContext();

  const search = useSelector(
    (state: AppState) => state.areas.category.search as SearchState
  );

  const {
    inputValue,
    filteredCore,
    filteredSections,
    filteredValues,
    disabledExpansions,
  } = search;

  const isBrandedCategory = Boolean(props.brand);
  const isSearching = inputValue.length > 1;

  return (
    <S.FilterWrapper>
      <ScrollElement name="Filter">
        <S.HeaderWrapper mobileOpen={mobileOpen}>
          <S.Header>
            <PanelHeader>
              <PanelTitle>{t('filter', 'filter.title')}</PanelTitle>
            </PanelHeader>
          </S.Header>

          <SelectedFilters categoryId={props.categoryId} />

          <S.CloseButton
            type="button"
            onClick={closeMobileFilter}
            mobileOpen={mobileOpen}
          >
            <IconClose />
          </S.CloseButton>
        </S.HeaderWrapper>

        {props.quickFilters.length > 0 && (
          <div>
            {props.quickFilters.map(c => (
              <ListItem
                key={c.name}
                name={c.name}
                link={c.pathName}
                showArrow
              />
            ))}
          </div>
        )}

        <div>
          <S.Input
            placeholder={t('filter', 'properties.search-placeholder')}
            value={inputValue}
            onChange={event => {
              dispatch(propertySearch(event.target.value));
            }}
          />
        </div>

        <S.Filter>
          <div>
            {filteredCore.map(coreFilter => (
              <FilterPanel
                key={coreFilter.id}
                canCompare={canCompare}
                isBrandFilteredCategory={isBrandedCategory}
                displayFilter={coreFilter}
                filteredValues={filteredValues}
              />
            ))}
          </div>

          {filteredSections.map(section => {
            const showSection = isSearching
              ? disabledExpansions.indexOf(section.name) < 0 // Defaults to expanded
              : expandedCategories.indexOf(section.name) >= 0; // Defaults to collapsed

            // Remove empty sections
            if (
              section.children?.filter(
                subFilter =>
                  (subFilter.item_type === 'category' && subFilter.children) ||
                  (filteredValues &&
                    filteredValues[subFilter.id] &&
                    filteredValues[subFilter.id].max_matching_products)
              ).length === 0
            ) {
              return null;
            }

            return (
              <React.Fragment key={section.name}>
                <S.ButtonWrapper>
                  <S.ExpansionButton
                    type="button"
                    onClick={() =>
                      isSearching
                        ? dispatch(propertySearchToggleExpansion(section.name))
                        : dispatch(expandSection(section.name))
                    }
                  >
                    <div>{section.name}</div>
                  </S.ExpansionButton>
                  <S.ArrowButton
                    type="button"
                    onClick={() =>
                      isSearching
                        ? dispatch(propertySearchToggleExpansion(section.name))
                        : dispatch(expandSection(section.name))
                    }
                  >
                    {showSection ? <IconChevronUp /> : <IconChevronDown />}
                  </S.ArrowButton>
                </S.ButtonWrapper>
                {showSection && (
                  <FilterPanel
                    key={section.name}
                    isBrandFilteredCategory={isBrandedCategory}
                    displayFilter={section}
                    filteredValues={filteredValues}
                    hideName
                  />
                )}
              </React.Fragment>
            );
          })}
        </S.Filter>
      </ScrollElement>
      <ApplyFilterButton
        count={props.productCount}
        hasFiltered={hasFiltered}
        onClick={closeMobileFilter}
      />
    </S.FilterWrapper>
  );
};

export default Filter;
